import { useEffect, useMemo, useRef, useState } from "react";

export type LikeHeartProps = {
  id: number;
  userId: number | undefined;
  initialCount: number;
  onLike?: () => Promise<void> | void;
  className?: string;
  disabled?: boolean;
  vibrateTick?: () => void;
  vibrateSuccess?: () => void;
  compact?: boolean;     // делает кнопку ниже/уже
  fullWidth?: boolean;   // растягивает на всю ширину
};

export default function LikeHeart({
  id,
  userId,
  initialCount,
  onLike,
  className = "",
  disabled = false,
  vibrateTick,
  vibrateSuccess,
  compact = false,
  fullWidth = false,
}: LikeHeartProps) {
  const storageKey = useMemo(() => `liked:${id}:${userId ?? "anon"}`, [id, userId]);
  const [count, setCount] = useState(initialCount);
  const [liked, setLiked] = useState<boolean>(() => {
    try { return localStorage.getItem(storageKey) === "1"; } catch { return false; }
  });
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const reducedMotion = useMemo(() => matchMedia("(prefers-reduced-motion: reduce)").matches, []);

  const iconSize = compact ? 18 : 22;
  const pad = compact ? "px-2 py-1" : "px-3 py-1.5";
  const textCls = compact ? "text-[11px]" : "text-sm";
  const heightCls = compact ? "h-8" : "";
  const widthCls = fullWidth ? "w-full min-w-0 justify-center whitespace-nowrap" : "whitespace-nowrap";

  useEffect(() => {
    const id = "like-heart-keyframes";
    if (document.getElementById(id)) return;
    const style = document.createElement("style");
    style.id = id;
    style.innerHTML = `
      @keyframes heart-pop { 0%{transform:scale(1)} 35%{transform:scale(1.25)} 100%{transform:scale(1)} }
      @keyframes ring { 0%{transform:scale(0);opacity:.8} 60%{opacity:.4} 100%{transform:scale(1);opacity:0} }
      @keyframes particle { 
        0%{transform:translate(0,0) scale(1);opacity:1}
        100%{transform:translate(var(--dx), var(--dy)) scale(0.6); opacity:0}
      }
    `;
    document.head.appendChild(style);
  }, []);

  const spawnRing = () => {
    if (!btnRef.current) return;
    const ring = document.createElement("span");
    ring.className = "pointer-events-none absolute inset-0 rounded-full bg-pink-500/20";
    ring.style.animation = "ring 500ms ease-out forwards";
    btnRef.current.appendChild(ring);
    setTimeout(() => ring.remove(), 600);
  };

  const spawnParticles = () => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const n = 10;
    for (let i = 0; i < n; i++) {
      const angle = (i / n) * Math.PI * 2;
      const dist = 20 + Math.random() * 12;
      const dx = Math.cos(angle) * dist;
      const dy = Math.sin(angle) * dist;
      const p = document.createElement("span");
      p.className = "pointer-events-none absolute w-1.5 h-1.5 rounded-full bg-pink-400";
      p.style.left = `${cx}px`;
      p.style.top = `${cy}px`;
      p.style.translate = "-50% -50%";
      p.style.setProperty("--dx", `${dx}px`);
      p.style.setProperty("--dy", `${dy}px`);
      p.style.animation = "particle 550ms ease-out forwards";
      btnRef.current.appendChild(p);
      setTimeout(() => p.remove(), 600);
    }
  };

  const animate = () => {
    if (reducedMotion || !btnRef.current) return;
    btnRef.current.style.animation = "heart-pop 320ms cubic-bezier(.2,.7,.3,1)";
    spawnRing();
    spawnParticles();
    setTimeout(() => { if (btnRef.current) btnRef.current.style.animation = ""; }, 340);
  };

  const handleClick = async () => {
    if (disabled || liked) return;
    vibrateTick?.();
    setLiked(true);
    setCount((c) => c + 1);
    try { localStorage.setItem(storageKey, "1"); } catch {}
    animate();

    try {
      await onLike?.();
      vibrateSuccess?.();
    } catch {
      setLiked(false);
      setCount((c) => Math.max(0, c - 1));
      try { localStorage.removeItem(storageKey); } catch {}
    }
  };

  return (
    <button
      ref={btnRef}
      onClick={handleClick}
      aria-pressed={liked}
      aria-label={liked ? "Понравилось" : "Нравится"}
      disabled={disabled || liked}
      className={[
        "relative inline-flex select-none items-center gap-1.5 rounded-full border transition",
        pad, heightCls, widthCls,
        liked ? "border-pink-500/40 bg-pink-500/10" : "border-white/10 hover:border-white/20",
        disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
        className,
      ].join(" ")}
    >
      <span className="relative inline-grid place-items-center">
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24"
             className={liked ? "drop-shadow-[0_0_10px_rgba(244,114,182,0.35)]" : ""}>
          <path
            d="M12.1 8.64L12 8.77l-.1-.13C10.14 6.6 7.11 6.28 5.5 8.09c-1.69 1.9-1.02 4.98 1.54 7.36C8.3 16.61 10.02 17.8 12 19c1.98-1.2 3.7-2.39 4.96-3.55 2.56-2.38 3.23-5.46 1.54-7.36-1.61-1.81-4.64-1.49-6.4.55Z"
            fill={liked ? "currentColor" : "none"}
            stroke="currentColor"
            strokeWidth="1.6"
            className={liked ? "text-pink-500" : "text-white/80"}
          />
        </svg>
      </span>
      <span className={`${textCls} tabular-nums`}>{count}</span>
    </button>
  );
}
